import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	getMomentTz,
} from "@dexteel/mesf-core";
import { Button, CircularProgress, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback, useEffect, useState } from "react";
import { useModalStyles } from "../../../../styles/modalStyles";
import { useCastersContext } from "../../../CastersContext";
import { PourbackSection } from "./PourbackSection";
import { ProcessData } from "./ProcessData";
import { ProductBatchSection } from "./ProductBatch/ProductBatch";
import { useDeliveryTicket } from "./hooks/useDeliveryTicket";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	processId: number;
	ladleClose?: Date | null;
}
export interface ProcessDataChanges {
	gradeId?: number | null;
	moldSizeId?: number | null;
	casterForeman?: string | null;
	melter?: string | null;
	labTech?: string | null;
	sequence?: string | null;
}
const moment = getMomentTz();

export const DeliveryTicketModal = ({
	show,
	onHide,
	processId,
	ladleClose,
}: Props) => {
	const classes = useModalStyles();
	const { actions } = useCastersContext();

	const {
		isLoading,
		error,
		setError,
		deliveryTicket,
		moldSizes,
		errors,
		dateTimeFormatted,
		handleHeatInputChange,
		handleProductBatchChange,
		handleClickPrint,
		saveAll,
		refreshProductBatches,
		setErrors,
		setPendingChanges,
		loadData,
		confirmationDialogOpen,
		confirmationDialogMessage,
		handleConfirmationDialogClose,
		// Pourback
		pourbackReasons,
	} = useDeliveryTicket(processId, show);

	const [pourbackFormData, setPourbackFormData] = useState<{
		weight: number | null;
		reasonId: number | null;
	} | null>(null);

	const handlePourbackChange = useCallback(
		(weight: number | null, reasonId: number | null) => {
			setPourbackFormData({ weight, reasonId });
		},
		[],
	);

	useEffect(() => {
		if (show) {
			loadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);
	return (
		<>
			{show && <HelmetDexteel title={`Delivery Ticket`} />}
			<MesfModal
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="delivery-ticket-modal"
				maxWidth="md"
			>
				{isLoading ? (
					<Grid
						container
						justifyContent="center"
						alignItems="center"
						style={{ padding: "560px 450px" }}
					>
						<CircularProgress size="5rem" />
					</Grid>
				) : (
					<>
						<MesfModal.Content
							className={`${classes.modalContent} ${classes.pageBreakAvoid}`}
							style={{
								border: "4px solid #ccc",
								borderRadius: 10,
								margin: "10px 20px",
							}}
						>
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								direction="column"
								spacing={1}
								style={{ marginBottom: "30px" }}
							>
								<Grid>
									<Typography
										variant="h6"
										align="center"
										style={{ fontWeight: "bold" }}
									>
										STEEL OF WEST VIRGINIA
									</Typography>
								</Grid>
								<Grid>
									<Typography
										variant="h6"
										align="center"
										style={{ fontWeight: "bold" }}
									>
										MELTSHOP DELIVERY TICKET
									</Typography>
								</Grid>
							</Grid>
							<form noValidate autoComplete="off">
								<Grid container spacing={2} justifyContent="center">
									{/* Top fields */}
									<ProcessData
										dateTimeFormatted={dateTimeFormatted}
										setPendingChanges={setPendingChanges}
										deliveryTicket={deliveryTicket}
										handleHeatInputChange={handleHeatInputChange}
										errors={errors}
										setErrors={setErrors}
									/>

									{/* Lower section for strands */}
									<ProductBatchSection
										errors={errors}
										setErrors={setErrors}
										deliveryTicket={deliveryTicket}
										moldSizes={moldSizes}
										handleProductBatchChange={handleProductBatchChange}
										refreshProductBatches={refreshProductBatches}
										isLoading={isLoading}
										ladleClose={ladleClose}
									/>
									<Divider style={{ margin: "10px 0", width: "90%" }} />
									{/* Pourback section */}
									<PourbackSection
										processId={processId}
										heatId={deliveryTicket?.heatData.HeatId ?? 0}
										pourbackId={deliveryTicket?.heatData.PourbackId ?? null}
										tapTime={deliveryTicket?.heatData.TapTime ?? null}
										initialPourbackWeight={
											deliveryTicket?.heatData.PourbackWeight ?? null
										}
										initialPourbackReasonId={
											deliveryTicket?.heatData.PourbackReasonId ?? null
										}
										pourbackReasons={pourbackReasons}
										onSaveSuccess={loadData}
										onPourbackChange={handlePourbackChange}
									/>
								</Grid>
							</form>
						</MesfModal.Content>

						<MesfModal.Actions
							className={classes.noPrint}
							style={{ padding: "1rem 3rem" }}
						>
							<Grid container spacing={3} justifyContent="flex-end">
								<Grid size={{ xs: 3, md: 3 }} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => {
											actions.setRefreshInPause(false);
											errors.clear();
											onHide(true);
										}}
									>
										Close
									</Button>
								</Grid>
								<Grid size={{ xs: 3, md: 3 }} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => saveAll(pourbackFormData)}
										disabled={!processId || isLoading}
									>
										Save
									</Button>
								</Grid>
								<Grid size={{ xs: 3, md: 6 }} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => handleClickPrint(pourbackFormData)}
										disabled={!processId || isLoading}
									>
										Save & print
									</Button>
								</Grid>
							</Grid>
						</MesfModal.Actions>
					</>
				)}
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<ConfirmationDialogRaw
				open={confirmationDialogOpen}
				onClose={(confirmed) =>
					handleConfirmationDialogClose(confirmed, pourbackFormData)
				}
				title="Confirm Weight"
				message={confirmationDialogMessage}
			/>
		</>
	);
};
